import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonSelectOption,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAtom } from 'jotai';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';
import * as Yup from 'yup';

import ConfirmationModal from '../components/confirmationmodal/ConfirmationModal';
import LoadableContent from '../components/content/LoadableContent';
import DefaultInput from '../components/forms/defaultinput/DefaultInput';
import DefaultSelect from '../components/forms/defaultselect/DefaultSelect';
import LoadingButton from '../components/loadingbutton/LoadingButton';
import Page from '../components/page/Page';
import { TransServerMessage } from '../components/translations/TransServerMessage';
import YesNo from '../components/yesno/YesNo';
import { IInstallationRouteProps } from '../interfaces/IRoute';
import {
    accessTokenAtom,
    jobDetailsWithHouseAndLeasingTypesAndStateAtom,
    jobIdAtom,
} from '../store/Atoms';
import { docRequest, trackActionsRequest } from '../store/Requests';
import { isCeeEnabled, isQuoteSigned } from '../utils/cee';
import { useCustomRouter } from '../utils/hooks/useCustomRouter';

const modalMessages = {
    ceeIsOlderThanTwoYears: (
        <Trans>
            Are you sure the building was built less than two years ago?
        </Trans>
    ),
    ceeIsClientMainResidence: (
        <>
            <p>
                <Trans>
                    A residential site includes secondary homes, airBnB or any
                    other non-business use.
                </Trans>
            </p>
            <p>
                <Trans>
                    Are you sure this site is linked to a business usage (like
                    a medical or law cabinet)?
                </Trans>
            </p>
        </>
    ),
    houseType: <Trans>Are you sure the house type is an office?</Trans>,
    leasingTypeOffice: <Trans>Are you sure the leasing type is office?</Trans>,
    leasingTypeOther: (
        <Trans>Are you sure the client is neither owner nor tenant?</Trans>
    ),
};

type CeeEligibilityFormValues = {
    ceeHasInstallableBathroom?: boolean;
    ceeIsClientMainResidence?: boolean;
    ceeIsOlderThanTwoYears?: boolean;
    houseType?: string;
    leasingType?: string;
    surface?: number;
    nbHeatings?: number;
};

const CeeEligibilitySchema = () =>
    Yup.object().shape({
        ceeHasInstallableBathroom: Yup.boolean().required(
            t`Please answer the question`
        ),
        ceeIsClientMainResidence: Yup.boolean().required(
            t`Please answer the question`
        ),
        ceeIsOlderThanTwoYears: Yup.boolean().required(
            t`Please answer the question`
        ),
        houseType: Yup.string().required(t`Please select house type`),
        leasingType: Yup.string().required(t`Please select leasing type`),
        surface: Yup.number()
            .typeError(t`Surface must be a number`)
            .min(0, t`Surface is too small! Min 0`)
            .max(1000, t`Surface is too high! Max 1000`)
            .required(t`Surface is required`),
        nbHeatings: Yup.number()
            .typeError(t`Number of heaters must be a number`)
            .integer(t`Number of heaters must be an integer`)
            .min(2, t`Number of heaters is too small! Min 2`)
            .max(30, t`Number of heaters is too high! Max 30`)
            .required(t`Number of heaters is required`),
    });

const CeeEligibility: React.FC<IInstallationRouteProps> = memo(() => {
    const router = useCustomRouter();
    const [isModalOpen, setModalIsOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState<JSX.Element>(<></>);
    const [currentElement, setCurrentElement] = useState('');
    const [value] = useAtom(jobDetailsWithHouseAndLeasingTypesAndStateAtom);
    const [jobId] = useAtom(jobIdAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [presentToast] = useIonToast();
    const houseTypes = useMemo(
        () => (value.state === 'hasData' ? value.data.houseTypes : []),
        [value]
    );
    const leasingTypes = useMemo(
        () => (value.state === 'hasData' ? value.data.leasingTypes : []),
        [value]
    );
    const jobDetails = useMemo(
        () => (value.state === 'hasData' ? value.data.jobDetails : undefined),
        [value]
    );
    const photoList: string[] = useMemo(
        () => (value.state === 'hasData' ? value.data.photoList : []),
        [value]
    );
    const typeInstallation =
        value.state === 'hasData' && jobDetails
            ? jobDetails.typeInstallation
            : '';
    const organisationalUnit =
        value.state === 'hasData' && jobDetails
            ? jobDetails.organisationalUnit
            : undefined;
    const hasCee = isCeeEnabled(
        typeInstallation,
        organisationalUnit ? organisationalUnit[2].hasCee : false
    );

    const hasQuoteSignature = useMemo(
        () => (photoList ? isQuoteSigned(photoList) : false),
        [photoList]
    );

    const handleFormSubmit = useCallback(
        async (
            data: CeeEligibilityFormValues,
            actions: FormikHelpers<CeeEligibilityFormValues>
        ) => {
            actions.setSubmitting(true);
            try {
                await docRequest(accessToken, jobId, data);
                trackActionsRequest(`generate_cee_quote`, {
                    ceeHasInstallableBathroom: data.ceeHasInstallableBathroom,
                    ceeIsClientMainResidence: data.ceeIsClientMainResidence,
                    ceeIsOlderThanTwoYears: data.ceeIsOlderThanTwoYears,
                });
                router.push(`/${jobId}/cee-quote`, 'forward');
            } catch (e) {
                presentToast({
                    message: t`Oops, something went wrong while saving CEE data`,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            actions.setSubmitting(false);
        },
        [accessToken, jobId, presentToast, router]
    );

    const handleModalNoAnswer = useCallback(
        async (formValues: CeeEligibilityFormValues) => {
            switch (currentElement) {
                case 'ceeIsOlderThanTwoYears':
                    formValues.ceeIsOlderThanTwoYears = true;
                    break;
                case 'ceeIsClientMainResidence':
                    formValues.ceeIsClientMainResidence = true;
                    break;
                case 'houseType':
                    formValues.houseType = undefined;
                    break;
                case 'leasingType':
                    formValues.leasingType = undefined;
                    break;
            }
            setModalIsOpen(false);
        },
        [currentElement]
    );

    return (
        <Page
            title={t`Quote Computation`}
            ionButtons={
                <IonButtons slot="start">
                    <IonBackButton text={t`Back`} />
                </IonButtons>
            }
            withPadding={true}
        >
            <LoadableContent value={value}>
                {hasCee ? (
                    hasQuoteSignature ? (
                        <Redirect
                            exact
                            to={{
                                pathname: `/${jobId}/installation/inspect`,
                            }}
                        />
                    ) : (
                        <Formik
                            // initialValues={{
                            //     ceeHasInstallableBathroom: undefined,
                            //     ceeIsClientMainResidence: undefined,
                            //     ceeIsOlderThanTwoYears: undefined,
                            //     surface: undefined,
                            //     nbHeatings: undefined,
                            // }}
                            initialValues={{}}
                            validationSchema={CeeEligibilitySchema()}
                            validateOnMount={true}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                handleChange,
                                handleBlur,
                                isSubmitting,
                                isValid,
                                setFieldValue,
                                errors,
                                touched,
                            }) => (
                                <Form>
                                    <YesNo
                                        label={t`Can the bathroom be installed in accordance to
                    safety regulations?`}
                                        value={
                                            values.ceeHasInstallableBathroom
                                        }
                                        onChange={(value) => {
                                            setFieldValue(
                                                'ceeHasInstallableBathroom',
                                                value
                                            );
                                        }}
                                    />
                                    <YesNo
                                        label={t`Is the building at least two years old?`}
                                        value={values.ceeIsOlderThanTwoYears}
                                        onChange={(value) => {
                                            const fieldName =
                                                'ceeIsOlderThanTwoYears';
                                            if (!value) {
                                                setCurrentElement(fieldName);
                                                setModalMessage(
                                                    modalMessages[fieldName]
                                                );
                                                setModalIsOpen(true);
                                            }
                                            setFieldValue(fieldName, value);
                                        }}
                                    />
                                    <YesNo
                                        label={t`Is the client living in the site?`}
                                        value={values.ceeIsClientMainResidence}
                                        onChange={(value) => {
                                            const fieldName =
                                                'ceeIsClientMainResidence';
                                            if (!value) {
                                                setCurrentElement(fieldName);
                                                setModalMessage(
                                                    modalMessages[fieldName]
                                                );
                                                setModalIsOpen(true);
                                            }
                                            setFieldValue(fieldName, value);
                                        }}
                                    />
                                    <DefaultSelect
                                        className={classNames({
                                            'ion-invalid': errors.houseType,
                                            'ion-touched': touched.houseType,
                                        })}
                                        name="houseType"
                                        value={values.houseType}
                                        label={t`House type`}
                                        placeholder={t`Select house type`}
                                        onIonChange={(e) => {
                                            if (e.detail.value === 'office') {
                                                const target =
                                                    e.target as HTMLIonSelectElement;
                                                const fieldName = target.name;

                                                setCurrentElement(fieldName);
                                                setModalMessage(
                                                    modalMessages['houseType']
                                                );
                                                setModalIsOpen(true);
                                            }
                                            return handleChange(e);
                                        }}
                                        onIonBlur={handleBlur}
                                        errorText={errors.houseType}
                                    >
                                        {houseTypes.map((houseType) => (
                                            <IonSelectOption
                                                key={houseType.houseTypeId}
                                                value={houseType.houseTypeId}
                                            >
                                                <TransServerMessage
                                                    id={
                                                        houseType.houseTypeName
                                                    }
                                                />
                                            </IonSelectOption>
                                        ))}
                                    </DefaultSelect>
                                    <DefaultSelect
                                        className={classNames(
                                            'ion-margin-top',
                                            {
                                                'ion-invalid':
                                                    errors.leasingType,
                                                'ion-touched':
                                                    touched.leasingType,
                                            }
                                        )}
                                        name="leasingType"
                                        value={values.leasingType}
                                        label={t`Leasing type`}
                                        placeholder={t`Select leasing type`}
                                        onIonChange={(e) => {
                                            if (
                                                e.detail.value === 'office' ||
                                                e.detail.value === 'other'
                                            ) {
                                                const target =
                                                    e.target as HTMLIonSelectElement;
                                                setCurrentElement(target.name);

                                                switch (e.detail.value) {
                                                    case 'office':
                                                        setModalMessage(
                                                            modalMessages[
                                                                'leasingTypeOffice'
                                                            ]
                                                        );
                                                        break;
                                                    case 'other':
                                                        setModalMessage(
                                                            modalMessages[
                                                                'leasingTypeOther'
                                                            ]
                                                        );
                                                        break;
                                                }

                                                setModalIsOpen(true);
                                            }
                                            return handleChange(e);
                                        }}
                                        onIonBlur={handleBlur}
                                        errorText={errors.leasingType}
                                    >
                                        {leasingTypes.map((leasingType) => (
                                            <IonSelectOption
                                                key={leasingType.leasingTypeId}
                                                value={
                                                    leasingType.leasingTypeId
                                                }
                                            >
                                                <TransServerMessage
                                                    id={
                                                        leasingType.leasingTypeName
                                                    }
                                                />
                                            </IonSelectOption>
                                        ))}
                                    </DefaultSelect>
                                    <DefaultInput
                                        className={classNames(
                                            'ion-margin-top',
                                            {
                                                'ion-invalid': errors.surface,
                                                'ion-touched': touched.surface,
                                            }
                                        )}
                                        name="surface"
                                        value={values.surface}
                                        label={t`House Surface Area (m²)`}
                                        onIonInput={handleChange}
                                        errorText={errors.surface}
                                    ></DefaultInput>
                                    <DefaultInput
                                        className={classNames(
                                            'ion-margin-top',
                                            {
                                                'ion-invalid':
                                                    errors.nbHeatings,
                                                'ion-touched':
                                                    touched.nbHeatings,
                                            }
                                        )}
                                        name="nbHeatings"
                                        value={values.nbHeatings}
                                        label={t`Total number of heaters to be
                                        installed`}
                                        onIonInput={handleChange}
                                        errorText={errors.nbHeatings}
                                    ></DefaultInput>
                                    <LoadingButton
                                        className="ion-margin-top"
                                        disabled={!isValid}
                                        loading={isSubmitting}
                                        type="submit"
                                    >
                                        <Trans>Generate Quote</Trans>
                                    </LoadingButton>
                                    <ConfirmationModal
                                        onYes={() => setModalIsOpen(false)}
                                        onNo={async () => {
                                            await handleModalNoAnswer(values);
                                        }}
                                        isOpen={isModalOpen}
                                        modalMessage={modalMessage}
                                    />
                                </Form>
                            )}
                        </Formik>
                    )
                ) : (
                    <Redirect
                        exact
                        to={{
                            pathname: '/',
                            state: {
                                from: router.routeInfo.lastPathname || '/',
                            },
                        }}
                    />
                )}
                <IonButton
                    expand="block"
                    routerLink={`/${jobId}/installation/inspect/done-error`}
                    className="ion-margin-top"
                >
                    {t`I can't perform the installation`}
                </IonButton>
            </LoadableContent>
        </Page>
    );
});

export default CeeEligibility;
